import _ from "lodash"
import {
  z,
  ZodString,
  ZodError,
  ZodOptional,
  ZodObject,
  ZodNullable,
} from "zod"

import { ZodFormObjectTypeDescription } from "@tfg/types/constants/zodFromInputTypes"
import {
  cssColorRegex,
  CSSColorSchema,
} from "@tfg/types/schemas/communication-studio/cssStylesSchema"

export function flattenZodIssuesToNestedObject<T>(
  issues: ZodError["issues"]
): Partial<Record<keyof T, string>> {
  const nestedErrors: Partial<Record<keyof T, string>> = {}

  for (const issue of issues) {
    if (issue.path.length > 0 && issue.message) {
      _.set(nestedErrors, issue.path, issue.message)
    }
  }

  return nestedErrors
}

const getZodObjectTypeFromDescription = (
  field: z.ZodTypeAny
): [string, z.ZodTypeAny] | undefined => {
  let finalField = field
  if (field instanceof ZodOptional || field instanceof ZodNullable) {
    finalField = field._def.innerType
  }

  if (finalField instanceof ZodObject && finalField._def.description) {
    switch (finalField._def.description) {
      case ZodFormObjectTypeDescription.ZOD_FILE:
        return [ZodFormObjectTypeDescription.ZOD_FILE, field]
      case ZodFormObjectTypeDescription.ZOD_STYLING_CLASS:
        return [ZodFormObjectTypeDescription.ZOD_STYLING_CLASS, field]
      case ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE:
        return [ZodFormObjectTypeDescription.ZOD_CSS_VARIABLE, field]
      default:
        return undefined
    }
  }
  return undefined
}

export const getZodFieldType = (
  shape: z.ZodRawShape,
  key: string
): [string, z.ZodTypeAny] => {
  let field = shape[key]
  let describedType: [string, z.ZodTypeAny] | undefined

  if (field instanceof ZodOptional || field instanceof ZodNullable) {
    field = field._def.innerType
    describedType = getZodObjectTypeFromDescription(field)

    if (describedType) {
      return describedType
    } else {
      if (!field._def.innerType) {
        return [field._def.typeName, field]
      }
      return [field._def.innerType._def.typeName, field._def.innerType]
    }
  }

  describedType = getZodObjectTypeFromDescription(field)
  if (describedType) {
    return describedType
  }
  return [field._def.typeName, field]
}

export const getKeyFromPath = (path: string[]): string => {
  return path[path.length - 1]
}

export function getClassPropsShapeAndKey(
  field: z.ZodTypeAny
): [z.ZodRawShape, string[]] {
  let finalField = field
  if (field instanceof ZodOptional || field instanceof ZodNullable) {
    finalField = field._def.innerType
  }
  const classPropsShape = finalField._def.shape().props._def.shape()
  const classPropsKeys = Object.keys(classPropsShape)
  return [classPropsShape, classPropsKeys]
}

export function getCssVariableShape(field: z.ZodTypeAny): z.ZodTypeAny {
  let finalField = field
  if (field instanceof ZodOptional || field instanceof ZodNullable) {
    finalField = field._def.innerType
  }
  const cssFieldShape = finalField._def.shape().value
  if (cssFieldShape instanceof ZodOptional) {
    return cssFieldShape._def.innerType
  }
  return cssFieldShape
}

export function isCSSColorSchema(
  schema: z.ZodTypeAny
): schema is typeof CSSColorSchema {
  if (!(schema instanceof ZodString)) return false

  const checks = schema._def.checks ?? []
  return checks.some(
    (check) =>
      check.kind === "regex" && check.regex.source === cssColorRegex.source
  )
}

export function parseZodErrors<T>(
  errors: Record<string, string[] | undefined> | undefined
): Partial<Record<keyof T, string>> {
  if (!errors) return {}
  return Object.entries(errors).reduce(
    (acc, [key, val]) => {
      if (val?.length) acc[key as keyof T] = val[0]
      return acc
    },
    {} as Partial<Record<keyof T, string>>
  )
}

function shouldKeep<TOriginal>(
  value: TOriginal | undefined | (object & Record<string, never>)
): value is Exclude<TOriginal, undefined | (object & Record<string, never>)> {
  if (_.isUndefined(value)) {
    return false
  }
  if (_.isPlainObject(value) && _.isEmpty(value)) {
    return false
  }
  return true
}

type Cleaned<T> = T extends object
  ? { [K in keyof T]?: Cleaned<T[K]> } | undefined
  : T extends (infer U)[]
    ? Cleaned<U>[] | undefined
    : T | undefined

export function cleanArray<T>(array: T[]): Cleaned<T>[] | undefined {
  const cleanedItems = array.map((item) => cleanEmptyObjects(item))

  if (cleanedItems.length === 0) return undefined
  return cleanedItems as Cleaned<T>[]
}

function cleanObject<T extends Record<string, unknown>>(
  obj: T
): Cleaned<T> | undefined {
  const cleanedResult: Record<string, unknown> = {}

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key]
      const cleanedValue = cleanEmptyObjects(value)

      if (shouldKeep(cleanedValue)) {
        cleanedResult[key] = cleanedValue
      }
    }
  }

  if (_.isEmpty(cleanedResult)) {
    return undefined
  }

  return cleanedResult as Cleaned<T>
}

export function cleanEmptyObjects<T>(obj: T[]): Cleaned<T>[] | undefined
export function cleanEmptyObjects<T extends object>(
  obj: T
): Cleaned<T> | undefined
export function cleanEmptyObjects<T>(obj: T): Cleaned<T> | undefined
export function cleanEmptyObjects(obj: unknown): unknown {
  if (!_.isObject(obj) || obj === null) {
    return obj
  }

  if (_.isArray(obj)) {
    return cleanArray(obj)
  }

  return cleanObject(obj as Record<string, unknown>)
}
